import React from 'react';
import {
  Box,
  Container,
  Typography,
  Avatar,
  IconButton,
  SvgIcon,
} from '@mui/material';
import {
  CalendarToday,
  Twitter,
  LinkedIn,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const FONT = '"Inter", system-ui, -apple-system, sans-serif';
const ACCENT = '#10b981';

const BlueskyIcon: React.FC<{ fontSize?: 'small' | 'inherit' | 'medium' | 'large' }> = (props) => (
  <SvgIcon {...props} viewBox="0 0 568 501">
    <path d="M123.121 33.664C188.241 82.553 258.281 181.68 284 234.873c25.719-53.192 95.759-152.32 160.879-201.21C491.866-1.611 568-28.906 568 57.947c0 17.346-9.945 145.713-15.778 166.555-20.275 72.453-94.155 90.933-159.875 79.748C507.222 323.8 536.444 388.56 473.333 453.32c-119.86 122.992-172.272-30.859-185.702-70.281-2.462-7.227-3.614-10.608-3.631-7.733-.017-2.875-1.169.506-3.631 7.733-13.43 39.422-65.842 193.273-185.702 70.28-63.111-64.76-33.89-129.52 80.986-149.071-65.72 11.185-139.6-7.295-159.875-79.748C10.045 203.66.1 75.293.1 57.947.1-28.906 76.234-1.611 123.121 33.664z" />
  </SvgIcon>
);

const FOOTER_LINKS: { title: string; links: { label: string; path: string }[] }[] = [
  {
    title: 'Product',
    links: [
      { label: 'Features', path: '/features' },
      { label: 'Pricing', path: '/pricing' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About', path: '/about' },
      { label: 'Contact', path: '/contact' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Privacy Policy', path: '/privacy' },
      { label: 'Terms of Service', path: '/terms' },
    ],
  },
];

const Footer: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: '#0f172a', borderTop: '1px solid rgba(255,255,255,0.06)', py: 5 }}>
      <Container maxWidth="lg">
        <Box sx={{
          display: 'flex', flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between', alignItems: { xs: 'center', md: 'flex-start' },
          gap: 4,
        }}>
          {/* Brand */}
          <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, justifyContent: { xs: 'center', md: 'flex-start' }, cursor: 'pointer' }}
              onClick={() => navigate('/')}
            >
              <Avatar sx={{ width: 28, height: 28, bgcolor: ACCENT }}>
                <CalendarToday sx={{ fontSize: 16 }} />
              </Avatar>
              <Typography sx={{ fontFamily: FONT, fontWeight: 700, color: 'white', fontSize: '0.95rem' }}>
                Player Central
              </Typography>
            </Box>
            <Typography sx={{ fontFamily: FONT, fontSize: '0.8rem', color: 'rgba(255,255,255,0.35)' }}>
              The youth sports command center for busy families.
            </Typography>
          </Box>

          {/* Links */}
          <Box sx={{ display: 'flex', gap: { xs: 4, md: 8 } }}>
            {FOOTER_LINKS.map((col) => (
              <Box key={col.title}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 600, color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.5 }}>
                  {col.title}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {col.links.map((link) => (
                    <Typography
                      key={link.label}
                      onClick={() => navigate(link.path)}
                      sx={{
                        fontFamily: FONT, fontSize: '0.85rem', color: 'rgba(255,255,255,0.4)',
                        cursor: 'pointer', '&:hover': { color: 'rgba(255,255,255,0.7)' },
                        transition: 'color 0.2s',
                      }}
                    >
                      {link.label}
                    </Typography>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>

          {/* Social */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {[Twitter, BlueskyIcon, LinkedIn].map((Icon, i) => (
              <IconButton key={i} size="small" sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: 'rgba(255,255,255,0.6)' } }}>
                <Icon fontSize="small" />
              </IconButton>
            ))}
          </Box>
        </Box>

        <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid rgba(255,255,255,0.06)', textAlign: 'center' }}>
          <Typography sx={{ fontFamily: FONT, fontSize: '0.75rem', color: 'rgba(255,255,255,0.25)' }}>
            &copy; {new Date().getFullYear()} Eleventh Triangular
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
